import { useEffect, useState, useRef } from 'react'
import { Plus, Edit2, Trash2, X, Image as ImageIcon, Save, Eye, EyeOff, Calendar } from 'lucide-react'
import { supabase, uploadPublic } from '../../lib/supabaseClient'
import ConfirmModal from '../../components/ConfirmModal.jsx'
import RichTextEditor from '../../components/RichTextEditor.jsx'

// MultiSelect Component Helper
const MultiSelectDropdown = ({ options, selected, onChange, placeholder, disabled }) => {
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef(null)

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const toggleOption = (val) => {
    const isSelected = selected.includes(val)
    if (isSelected) {
      onChange(selected.filter(i => i !== val))
    } else {
      onChange([...selected, val])
    }
  }

  const selectAll = () => onChange(options.map(o => o.value))
  const clearAll = () => onChange([])

  return (
    <div className="relative" ref={dropdownRef}>
      <div 
        onClick={() => !disabled && setIsOpen(!isOpen)}
        className={`w-full bg-slate-950 border border-slate-800 p-3 rounded-lg text-white transition flex items-center justify-between min-h-[46px] ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:border-slate-700'}`}
      >
        <div className="flex flex-wrap gap-1 truncate max-w-[85%]">
          {selected.length === 0 ? (
            <span className="text-slate-500">{placeholder}</span>
          ) : selected.length === options.length ? (
            <span className="text-teal-400 font-medium">Todos selecionados</span>
          ) : (
            <span className="text-slate-300">{selected.length} selecionado(s)</span>
          )}
        </div>
        <span className="text-slate-500 text-xs">▼</span>
      </div>
      
      {isOpen && (
        <div className="absolute z-[130] top-full left-0 right-0 mt-1 bg-slate-900 border border-slate-700 rounded-lg shadow-xl max-h-60 overflow-y-auto p-2">
          <div className="flex items-center justify-between pb-2 mb-2 border-b border-slate-800">
            <button onClick={selectAll} className="text-xs text-orange-400 hover:text-orange-300 font-bold px-2 py-1">Selecionar Todos</button>
            <button onClick={clearAll} className="text-xs text-slate-400 hover:text-slate-300 font-bold px-2 py-1">Limpar</button>
          </div>
          {options.length === 0 ? (
            <div className="p-3 text-center text-sm text-slate-500">Nenhuma opção disponível</div>
          ) : (
            options.map(opt => (
              <label key={opt.value} className="flex items-center gap-2 p-2 hover:bg-slate-800 rounded cursor-pointer transition">
                <input 
                  type="checkbox" 
                  checked={selected.includes(opt.value)}
                  onChange={() => toggleOption(opt.value)}
                  className="w-4 h-4 rounded border-slate-700 bg-slate-950 text-orange-500 focus:ring-orange-500 focus:ring-offset-slate-900"
                />
                <span className="text-sm text-slate-300">{opt.label}</span>
              </label>
            ))
          )}
        </div>
      )}
    </div>
  )
}

export default function AdminNotices({ partner, role }) {
  const [notices, setNotices] = useState([])
  const [loading, setLoading] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [editingNotice, setEditingNotice] = useState(null)
  const [saving, setSaving] = useState(false)
  const [deletingId, setDeletingId] = useState(null)
  const [confirmDeleteId, setConfirmDeleteId] = useState(null)
  const fileInputRef = useRef(null)
  const [showPreview, setShowPreview] = useState(false)
  const [previewNotice, setPreviewNotice] = useState(null)

  // Options Data
  const [options, setOptions] = useState({
    partners: [],
    companies: [],
    roles: [],
    courses: []
  })
  const [filters, setFilters] = useState({ status: 'all', q: '', start: '', end: '' })

  const [formData, setFormData] = useState({
    title: '',
    content: '',
    image_url: '',
    is_active: true,
    target_partners: [],
    target_companies: [],
    target_roles: [],
    target_courses: [],
    valid_from: '',
    valid_until: '',
    has_button: false,
    button_text: '',
    button_link: '',
    no_expire: false
  })
  const [uploadingImage, setUploadingImage] = useState(false)

  const MAX_TITLE_CHARS = 100

  const fetchDataForFilters = async () => {
    try {
      const [partnersRes, companiesRes, coursesRes, rolesRes] = await Promise.all([
        supabase.from('sas_partners').select('id, name').eq('status', 'Ativo').order('name'),
        supabase.from('companies').select('id, name').eq('status', 'Ativo').order('name'),
        supabase.from('courses').select('id, name').eq('status', 'Ativo').order('name'),
        supabase.from('communication_roles').select('name').order('name')
      ])

      const builtinRoles = ['Admin', 'Coordenador', 'Instrutor', 'Parceiro SAS', 'Ponto Focal', 'Aluno']
      const customRoles = (rolesRes.data || []).map(r => r.name)
      const allRoles = [...new Set([...builtinRoles, ...customRoles])]

      setOptions({
        partners: (partnersRes.data || []).map(p => ({ value: p.id, label: p.name })),
        companies: (companiesRes.data || []).map(c => ({ value: c.id, label: c.name })),
        roles: allRoles.map(r => ({ value: r, label: r })),
        courses: (coursesRes.data || []).map(c => ({ value: c.id, label: c.name }))
      })
    } catch (err) {
      console.error('Erro ao buscar dados para filtros:', err)
    }
  }

  const fetchNotices = async () => {
    setLoading(true)
    try {
      const { data, error } = await supabase
        .from('system_notices')
        .select('*')
        .order('created_at', { ascending: false })
      if (error) throw error
      setNotices(data || [])
    } catch (err) {
      console.error('Erro ao buscar avisos:', err)
      // Se a tabela ainda não existir, ignoramos o erro silenciosamente no frontend 
      // ou mostramos um toast.
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchNotices()
    fetchDataForFilters()
  }, [])

  const handleOpenModal = (notice = null) => {
    if (notice) {
      setEditingNotice(notice)
      setFormData({
        title: notice.title || '',
        content: notice.content || '',
        image_url: notice.image_url || '',
        is_active: notice.is_active !== false,
        target_partners: notice.target_partners || [],
        target_companies: notice.target_companies || [],
        target_roles: notice.target_roles || [],
        target_courses: notice.target_courses || [],
        valid_from: notice.valid_from ? new Date(notice.valid_from).toISOString().slice(0, 16) : '',
        valid_until: notice.valid_until ? new Date(notice.valid_until).toISOString().slice(0, 16) : '',
        has_button: notice.has_button || false,
        button_text: notice.button_text || '',
        button_link: notice.button_link || '',
        no_expire: !notice.valid_from && !notice.valid_until
      })
    } else {
      setEditingNotice(null)
      setFormData({
        title: '',
        content: '',
        image_url: '',
        is_active: true,
        target_partners: [],
        target_companies: [],
        target_roles: [],
        target_courses: [],
        valid_from: '',
        valid_until: '',
        has_button: false,
        button_text: '',
        button_link: '',
        no_expire: false
      })
    }
    setShowModal(true)
  }

  const handleCloseModal = () => {
    setShowModal(false)
    setEditingNotice(null)
  }

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    // Validação básica de tamanho (máx 2MB)
    const maxBytes = 2 * 1024 * 1024
    if (file.size > maxBytes) {
      alert('A imagem deve ter no máximo 2MB.')
      if (fileInputRef.current) fileInputRef.current.value = ''
      return
    }
    setUploadingImage(true)
    try {
      const ext = file.name.split('.').pop()
      const path = `notices/${Date.now()}_${Math.random().toString(36).substring(2, 9)}.${ext}`
      const url = await uploadPublic(path, file)
      setFormData(prev => ({ ...prev, image_url: url }))
    } catch (error) {
      console.error('Erro no upload da imagem:', error)
      const msg = error?.message || 'Verifique se o bucket "communications" existe e possui políticas de escrita para Admin.'
      alert('Erro ao fazer upload da imagem: ' + msg)
    } finally {
      setUploadingImage(false)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  const handleSave = async () => {
    if (!formData.title.trim() || !formData.content.trim()) {
      alert('Título e conteúdo são obrigatórios.')
      return
    }

    setSaving(true)
    try {
      const payload = {
        title: formData.title,
        content: formData.content,
        image_url: formData.image_url,
        is_active: formData.is_active,
        target_partners: formData.target_partners,
        target_companies: formData.target_companies,
        target_roles: formData.target_roles,
        target_courses: formData.target_courses,
        valid_from: formData.no_expire ? null : (formData.valid_from ? new Date(formData.valid_from).toISOString() : null),
        valid_until: formData.no_expire ? null : (formData.valid_until ? new Date(formData.valid_until).toISOString() : null),
        has_button: formData.has_button,
        button_text: formData.has_button ? formData.button_text : null,
        button_link: formData.has_button ? formData.button_link : null
      }

      // Sinalização: limite de 5 avisos ativos por usuário
      if (payload.is_active) {
        try {
          const { data: warnData } = await supabase.rpc('admin_check_notice_capacity', { p_notice: payload })
          const exceeds = Number(warnData?.exceeds_count || 0)
          if (exceeds > 0) {
            alert(`Atenção: ${exceeds} usuário(s) já possuem 5 avisos ativos. Este novo aviso ultrapassará o limite para eles.`)
          }
        } catch (e) {
          console.warn('Aviso de capacidade não disponível:', e?.message || e)
        }
      }

      if (editingNotice) {
        const { error } = await supabase
          .from('system_notices')
          .update(payload)
          .eq('id', editingNotice.id)
        if (error) throw error
      } else {
        const { data: userData } = await supabase.auth.getUser()
        if (userData?.user) {
          payload.created_by = userData.user.id
        }
        const { error } = await supabase
          .from('system_notices')
          .insert(payload)
        if (error) throw error
      }
      
      await fetchNotices()
      handleCloseModal()
    } catch (err) {
      console.error('Erro ao salvar aviso:', err)
      const msg = err?.message || 'Falha ao salvar. Verifique sua sessão e as policies RLS.'
      alert('Erro ao salvar o aviso: ' + msg)
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!confirmDeleteId) return
    setDeletingId(confirmDeleteId)
    try {
      const { error } = await supabase
        .from('system_notices')
        .delete()
        .eq('id', confirmDeleteId)
      if (error) throw error
      await fetchNotices()
    } catch (err) {
      console.error('Erro ao excluir aviso:', err)
      alert('Erro ao excluir o aviso.')
    } finally {
      setDeletingId(null)
      setConfirmDeleteId(null)
    }
  }

  const toggleActive = async (id, currentStatus) => {
    try {
      const { error } = await supabase
        .from('system_notices')
        .update({ is_active: !currentStatus })
        .eq('id', id)
      if (error) throw error
      await fetchNotices()
    } catch (err) {
      console.error('Erro ao alternar status:', err)
    }
  }

  const handlePreview = (notice) => {
    setPreviewNotice(notice)
    setShowPreview(true)
  }

  return (
    <div className="animate-fade-in space-y-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 bg-slate-900 p-6 rounded-xl border border-slate-800">
        <div>
          <h2 className="text-xl font-bold text-white mb-1">Avisos do Sistema</h2>
          <p className="text-sm text-slate-400">Gerencie os avisos que serão exibidos para os usuários na plataforma.</p>
        </div>
        <button
          onClick={() => handleOpenModal()}
          className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-lg font-bold transition flex items-center gap-2"
        >
          <Plus size={18} />
          Novo Aviso
        </button>
      </div>

      <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <div className="p-4 border-b border-slate-800 grid grid-cols-1 md:grid-cols-5 gap-3 bg-slate-950/40">
            <div className="flex flex-col gap-1">
              <label className="text-[10px] uppercase font-bold text-slate-500">Status</label>
              <select
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                className="bg-slate-950 border border-slate-700 text-white text-sm rounded px-3 py-2 outline-none focus:border-orange-500"
              >
                <option value="all">Todos</option>
                <option value="active">Ativo</option>
                <option value="inactive">Inativo</option>
              </select>
            </div>
            <div className="flex flex-col gap-1 md:col-span-2">
              <label className="text-[10px] uppercase font-bold text-slate-500">Buscar por Título</label>
              <input
                type="text"
                value={filters.q}
                onChange={(e) => setFilters({ ...filters, q: e.target.value })}
                placeholder="Digite parte do título"
                className="bg-slate-950 border border-slate-700 text-white text-sm rounded px-3 py-2 outline-none focus:border-orange-500"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-[10px] uppercase font-bold text-slate-500">Criado de</label>
              <input
                type="datetime-local"
                value={filters.start}
                onChange={(e) => setFilters({ ...filters, start: e.target.value })}
                className="bg-slate-950 border border-slate-700 text-white text-sm rounded px-3 py-2 outline-none focus:border-orange-500 [color-scheme:dark]"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-[10px] uppercase font-bold text-slate-500">Até</label>
              <div className="flex gap-2">
                <input
                  type="datetime-local"
                  value={filters.end}
                  onChange={(e) => setFilters({ ...filters, end: e.target.value })}
                  className="flex-1 bg-slate-950 border border-slate-700 text-white text-sm rounded px-3 py-2 outline-none focus:border-orange-500 [color-scheme:dark]"
                />
                <button
                  onClick={() => setFilters({ status: 'all', q: '', start: '', end: '' })}
                  className="px-3 py-2 rounded bg-slate-800 text-white text-xs font-bold hover:bg-slate-700"
                >
                  Limpar
                </button>
              </div>
            </div>
          </div>
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-slate-950/50 border-b border-slate-800 text-xs uppercase tracking-wider text-slate-500 font-bold">
                <th className="p-4">Status</th>
                <th className="p-4">Título</th>
                <th className="p-4">Data de Criação</th>
                <th className="p-4">Período de Validade</th>
                <th className="p-4 text-right">Ações</th>
              </tr>
            </thead>
            <tbody className="text-sm">
              {loading ? (
                <tr>
                  <td colSpan="5" className="p-8 text-center text-slate-500">Carregando avisos...</td>
                </tr>
              ) : notices.length === 0 ? (
                <tr>
                  <td colSpan="5" className="p-8 text-center text-slate-500">Nenhum aviso cadastrado.</td>
                </tr>
              ) : (
                (() => {
                  const start = filters.start ? new Date(filters.start) : null
                  const end = filters.end ? new Date(filters.end) : null
                  const list = (notices || []).filter(n => {
                    // status
                    if (filters.status === 'active' && n.is_active === false) return false
                    if (filters.status === 'inactive' && n.is_active !== false) return false
                    // title
                    if (filters.q && !(n.title || '').toLowerCase().includes(filters.q.toLowerCase())) return false
                    // created_at period
                    if (start || end) {
                      const created = n.created_at ? new Date(n.created_at) : null
                      if (!created) return false
                      if (start && created < start) return false
                      if (end && created > end) return false
                    }
                    return true
                  })
                  const fmt = (d) => d ? new Date(d).toLocaleString('pt-BR') : null
                  const validityText = (n) => {
                    const vf = n.valid_from
                    const vu = n.valid_until
                    if (!vf && !vu) return 'Não expira'
                    if (vf && vu) return `${fmt(vf)} — ${fmt(vu)}`
                    if (vf) return `A partir de ${fmt(vf)}`
                    return `Até ${fmt(vu)}`
                  }
                  return list.map(notice => (
                  <tr key={notice.id} className="border-b border-slate-800/50 hover:bg-slate-800/20 transition group">
                    <td className="p-4">
                      <button 
                        onClick={() => toggleActive(notice.id, notice.is_active)}
                        className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider border transition-colors ${
                          notice.is_active !== false
                            ? 'bg-teal-500/10 text-teal-400 border-teal-500/20 hover:bg-teal-500/20' 
                            : 'bg-slate-800 text-slate-400 border-slate-700 hover:bg-slate-700'
                        }`}
                        title={notice.is_active !== false ? "Clique para desativar" : "Clique para ativar"}
                      >
                        {notice.is_active !== false ? <Eye size={12} /> : <EyeOff size={12} />}
                        {notice.is_active !== false ? 'Ativo' : 'Inativo'}
                      </button>
                    </td>
                    <td className="p-4 font-medium text-white">
                      {notice.title}
                    </td>
                    <td className="p-4 text-slate-400">
                      {new Date(notice.created_at).toLocaleString('pt-BR')}
                    </td>
                    <td className="p-4 text-slate-300">
                      {validityText(notice)}
                    </td>
                    <td className="p-4 text-right">
                      <div className="flex items-center justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={() => handlePreview(notice)}
                          className="p-1.5 text-slate-400 hover:text-white bg-slate-800 hover:bg-slate-700 rounded transition"
                          title="Visualizar"
                        >
                          <Eye size={16} />
                        </button>
                        <button
                          onClick={() => handleOpenModal(notice)}
                          className="p-1.5 text-slate-400 hover:text-white bg-slate-800 hover:bg-slate-700 rounded transition"
                          title="Editar"
                        >
                          <Edit2 size={16} />
                        </button>
                        <button
                          onClick={() => setConfirmDeleteId(notice.id)}
                          className="p-1.5 text-rose-400 hover:text-white bg-rose-500/10 hover:bg-rose-500 rounded transition"
                          title="Excluir"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                  ))
                })()
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showModal && (
        <div className="fixed inset-0 z-[120] flex items-center justify-center bg-slate-950/80 backdrop-blur-sm p-4 animate-fade-in">
          <div className="bg-slate-900 border border-slate-700 rounded-xl w-full max-w-6xl shadow-2xl flex flex-col max-h-[90vh]">
            <div className="flex justify-between items-center p-6 border-b border-slate-800">
              <h3 className="text-xl font-bold text-white">
                {editingNotice ? 'Editar Aviso' : 'Novo Aviso'}
              </h3>
              <button onClick={handleCloseModal} className="text-slate-500 hover:text-white transition">
                <X size={24} />
              </button>
            </div>
            
            <div className="p-6 overflow-y-auto custom-scrollbar flex-1">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                
                {/* Coluna Esquerda: Conteúdo Principal */}
                <div className="lg:col-span-7 space-y-6">
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <label className="text-xs font-bold text-slate-400 uppercase tracking-widest">
                        Título do Aviso <span className="text-rose-500">*</span>
                      </label>
                      <span className={`text-xs ${formData.title.length > MAX_TITLE_CHARS ? 'text-rose-500' : 'text-slate-500'}`}>
                        {formData.title.length} / {MAX_TITLE_CHARS}
                      </span>
                    </div>
                    <input
                      type="text"
                      value={formData.title}
                      onChange={(e) => {
                        if (e.target.value.length <= MAX_TITLE_CHARS) {
                          setFormData({...formData, title: e.target.value})
                        }
                      }}
                      className="w-full bg-slate-950 border border-slate-800 p-3 rounded-lg text-white outline-none focus:border-orange-500 transition"
                      placeholder="Ex: Atualização Importante do Sistema"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-xs font-bold text-slate-400 uppercase tracking-widest">
                      Conteúdo <span className="text-rose-500">*</span>
                    </label>
                    <div className="border border-slate-800 rounded-xl overflow-hidden">
                      <RichTextEditor 
                        value={formData.content}
                        onChange={(html) => setFormData({...formData, content: html})}
                        placeholder="Escreva a mensagem do aviso aqui..."
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-xs font-bold text-slate-400 uppercase tracking-widest flex items-center justify-between">
                      <span>Imagem de Destaque</span>
                      <span className="text-[10px] bg-blue-500/10 text-blue-400 px-2 py-0.5 rounded border border-blue-500/20 normal-case">
                        Dimensões: 800x400 (2:1)
                      </span>
                    </label>
                    <div className="flex flex-col gap-4 bg-slate-950 p-4 rounded-xl border border-slate-800">
                      {formData.image_url ? (
                        <div className="relative rounded-lg overflow-hidden border border-slate-700 group w-full max-w-md mx-auto aspect-[2/1] bg-slate-900 flex items-center justify-center">
                          <img src={formData.image_url} alt="Capa" className="w-full h-full object-cover" />
                          <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition flex items-center justify-center gap-4">
                            <button
                              onClick={() => fileInputRef.current?.click()}
                              className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-white text-sm font-bold rounded-lg transition"
                            >
                              Trocar
                            </button>
                            <button
                              onClick={() => setFormData({...formData, image_url: ''})}
                              className="px-4 py-2 bg-rose-600 hover:bg-rose-700 text-white text-sm font-bold rounded-lg transition"
                            >
                              Remover
                            </button>
                          </div>
                        </div>
                      ) : (
                        <button
                          onClick={() => fileInputRef.current?.click()}
                          disabled={uploadingImage}
                          className="w-full py-6 border-2 border-dashed border-slate-700 rounded-lg flex flex-col items-center justify-center gap-2 hover:border-orange-500 hover:bg-slate-900 transition text-slate-400 hover:text-white disabled:opacity-50"
                        >
                          {uploadingImage ? (
                            <div className="animate-spin w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full"></div>
                          ) : (
                            <>
                              <ImageIcon size={28} />
                              <span className="font-medium text-sm">Clique para selecionar imagem</span>
                              <span className="text-[10px] text-slate-500">PNG, JPG, WEBP (Máx 2MB)</span>
                            </>
                          )}
                        </button>
                      )}
                      <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleImageUpload}
                        accept="image/*"
                        className="hidden"
                      />
                    </div>
                  </div>
                </div>

                {/* Coluna Direita: Configurações */}
                <div className="lg:col-span-5 space-y-6">
                  
                  <div className="bg-slate-950 border border-slate-800 p-5 rounded-xl space-y-4">
                    <div className="flex items-center justify-between">
                      <h4 className="text-sm font-bold text-white uppercase tracking-widest flex items-center gap-2">
                        <Calendar size={16} className="text-orange-500" /> Período de Validade
                      </h4>
                      <label className="flex items-center gap-2 cursor-pointer select-none">
                        <input
                          type="checkbox"
                          checked={formData.no_expire}
                          onChange={(e) => {
                            const v = e.target.checked
                            setFormData({
                              ...formData,
                              no_expire: v,
                              valid_from: v ? '' : formData.valid_from,
                              valid_until: v ? '' : formData.valid_until,
                            })
                          }}
                          className="w-4 h-4 rounded border-slate-700 bg-slate-900 text-orange-500 focus:ring-orange-500 focus:ring-offset-slate-900"
                        />
                        <span className="text-xs font-bold text-slate-300 uppercase">Não Expira</span>
                      </label>
                    </div>
                    <div className="grid grid-cols-1 gap-4">
                      <div className="space-y-1.5">
                        <label className="text-xs font-bold text-slate-500 uppercase">A Partir De</label>
                        <input
                          type="datetime-local"
                          value={formData.valid_from}
                          onChange={(e) => setFormData({...formData, valid_from: e.target.value})}
                          disabled={formData.no_expire}
                          className={`w-full p-2.5 rounded-lg text-white text-sm outline-none transition [color-scheme:dark] ${formData.no_expire ? 'bg-slate-900/50 border border-slate-800 cursor-not-allowed opacity-60' : 'bg-slate-900 border border-slate-700 focus:border-orange-500'}`}
                        />
                      </div>
                      <div className="space-y-1.5">
                        <label className="text-xs font-bold text-slate-500 uppercase">Até</label>
                        <input
                          type="datetime-local"
                          value={formData.valid_until}
                          onChange={(e) => setFormData({...formData, valid_until: e.target.value})}
                          disabled={formData.no_expire}
                          className={`w-full p-2.5 rounded-lg text-white text-sm outline-none transition [color-scheme:dark] ${formData.no_expire ? 'bg-slate-900/50 border border-slate-800 cursor-not-allowed opacity-60' : 'bg-slate-900 border border-slate-700 focus:border-orange-500'}`}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="bg-slate-950 border border-slate-800 p-5 rounded-xl space-y-4">
                    <h4 className="text-sm font-bold text-white mb-1 uppercase tracking-widest">Público Alvo</h4>
                    <p className="text-[10px] text-slate-400 mb-4 leading-tight">Deixe os campos vazios para exibir o aviso para todos os usuários do sistema.</p>
                    
                    <div className="space-y-4">
                      <div className="space-y-1.5">
                        <label className="text-[10px] font-bold text-slate-500 uppercase">Perfis</label>
                        <MultiSelectDropdown
                          options={options.roles}
                          selected={formData.target_roles}
                          onChange={(vals) => setFormData({...formData, target_roles: vals})}
                          placeholder="Todos os perfis"
                        />
                      </div>
                      
                      <div className="space-y-1.5">
                        <label className="text-[10px] font-bold text-slate-500 uppercase">Cursos (Matriculados)</label>
                        <MultiSelectDropdown
                          options={options.courses}
                          selected={formData.target_courses}
                          onChange={(vals) => setFormData({...formData, target_courses: vals})}
                          placeholder="Todos os cursos"
                        />
                      </div>

                      <div className="space-y-1.5">
                        <label className="text-[10px] font-bold text-slate-500 uppercase">Parceiros SAS</label>
                        <MultiSelectDropdown
                          options={options.partners}
                          selected={formData.target_partners}
                          onChange={(vals) => setFormData({...formData, target_partners: vals})}
                          placeholder="Todos os parceiros"
                        />
                      </div>

                      <div className="space-y-1.5">
                        <label className="text-[10px] font-bold text-slate-500 uppercase">Empresas</label>
                        <MultiSelectDropdown
                          options={options.companies}
                          selected={formData.target_companies}
                          onChange={(vals) => setFormData({...formData, target_companies: vals})}
                          placeholder="Todas as empresas"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="bg-slate-950 border border-slate-800 p-5 rounded-xl space-y-4">
                    <div className="flex items-center gap-2">
                      <input 
                        type="checkbox" 
                        id="notice-has-button"
                        checked={formData.has_button}
                        onChange={(e) => setFormData({...formData, has_button: e.target.checked})}
                        className="w-4 h-4 rounded border-slate-700 bg-slate-900 text-orange-500 focus:ring-orange-500 focus:ring-offset-slate-900"
                      />
                      <label htmlFor="notice-has-button" className="text-sm font-bold text-white uppercase tracking-widest cursor-pointer">
                        Botão de Ação (CTA)
                      </label>
                    </div>

                    {formData.has_button && (
                      <div className="space-y-4 pt-2 animate-fade-in">
                        <div className="space-y-1.5">
                          <label className="text-[10px] font-bold text-slate-500 uppercase">
                            Nome do Botão <span className="text-rose-500">*</span>
                          </label>
                          <input
                            type="text"
                            value={formData.button_text}
                            onChange={(e) => setFormData({...formData, button_text: e.target.value})}
                            className="w-full bg-slate-900 border border-slate-700 p-2.5 rounded-lg text-white text-sm outline-none focus:border-orange-500 transition"
                            placeholder="Ex: Acessar Curso"
                          />
                        </div>
                        <div className="space-y-1.5">
                          <label className="text-[10px] font-bold text-slate-500 uppercase">
                            Link do Botão <span className="text-rose-500">*</span>
                          </label>
                          <input
                            type="url"
                            value={formData.button_link}
                            onChange={(e) => setFormData({...formData, button_link: e.target.value})}
                            className="w-full bg-slate-900 border border-slate-700 p-2.5 rounded-lg text-white text-sm outline-none focus:border-orange-500 transition"
                            placeholder="Ex: https://sofera.com.br/curso/123"
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="bg-slate-950 border border-slate-800 p-5 rounded-xl">
                    <div className="flex items-center gap-2">
                      <input 
                        type="checkbox" 
                        id="notice-active"
                        checked={formData.is_active}
                        onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                        className="w-4 h-4 rounded border-slate-700 bg-slate-900 text-orange-500 focus:ring-orange-500 focus:ring-offset-slate-900"
                      />
                      <label htmlFor="notice-active" className="text-sm font-bold text-white uppercase tracking-widest cursor-pointer">
                        Aviso Ativo (Visível)
                      </label>
                    </div>
                  </div>

                </div>
              </div>
            </div>
            
            <div className="p-6 border-t border-slate-800 flex justify-end gap-3 bg-slate-900/50 rounded-b-xl shrink-0">
              <button
                onClick={handleCloseModal}
                className="px-6 py-2.5 rounded-lg text-slate-300 font-bold hover:bg-slate-800 transition"
              >
                Cancelar
              </button>
              <button
                onClick={handleSave}
                disabled={saving || !formData.title.trim() || !formData.content.trim() || (formData.has_button && (!formData.button_text.trim() || !formData.button_link.trim()))}
                className="px-6 py-2.5 rounded-lg bg-orange-500 hover:bg-orange-600 text-white font-bold transition flex items-center gap-2 disabled:opacity-50"
              >
                {saving ? (
                  <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full"></div>
                ) : (
                  <Save size={18} />
                )}
                {editingNotice ? 'Salvar Alterações' : 'Criar Aviso'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showPreview && previewNotice && (
        <div className="fixed inset-0 z-[130] flex items-center justify-center bg-slate-950/80 backdrop-blur-sm p-4 animate-fade-in">
          <div className="bg-slate-900 border border-slate-700 rounded-xl w-full max-w-3xl shadow-2xl overflow-hidden">
            <div className="p-4 border-b border-slate-800">
              <div className="flex items-center justify-between">
                <div className="flex flex-col">
                  <span className="text-[11px] uppercase tracking-widest text-slate-400">Pré-visualização</span>
                  <h3 className="text-xl font-extrabold text-white mt-1">{previewNotice.title}</h3>
                </div>
                <button onClick={() => setShowPreview(false)} className="text-slate-400 hover:text-white"><X size={20} /></button>
              </div>
            </div>
            <div className="p-0">
              {previewNotice.image_url && (
                <img
                  src={previewNotice.image_url}
                  alt="Capa"
                  className="w-full block"
                  style={{ aspectRatio: '2 / 1', objectFit: 'contain', backgroundColor: '#0f172a' }}
                />
              )}
              <div className="p-6 space-y-4">
                {!previewNotice.image_url && (
                  <h4 className="text-xl font-extrabold text-white">{previewNotice.title}</h4>
                )}
                <div className="prose prose-invert max-w-none" dangerouslySetInnerHTML={{ __html: previewNotice.content || '' }} />
                {previewNotice.has_button && previewNotice.button_text && previewNotice.button_link && (
                  <div>
                    <a
                      href={previewNotice.button_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-block px-4 py-2 rounded-lg bg-orange-500 hover:bg-orange-600 text-white font-bold transition"
                    >
                      {previewNotice.button_text}
                    </a>
                  </div>
                )}
              </div>
            </div>
            <div className="p-4 border-t border-slate-800 flex justify-end gap-3">
              <button
                onClick={async () => {
                  try {
                    const { data: authData } = await supabase.auth.getUser()
                    const uid = authData?.user?.id
                    if (!uid) throw new Error('Sessão expirada')
                    await supabase.from('system_notice_reads').upsert({ user_id: uid, notice_id: previewNotice.id, read_at: new Date().toISOString() }, { onConflict: 'user_id,notice_id' })
                    alert('Marcado como lido para este usuário.')
                  } catch (e) {
                    console.error('Marcar como lido:', e)
                    alert('Falha ao marcar como lido: ' + (e?.message || 'erro desconhecido'))
                  }
                }}
                className="px-4 py-2 rounded bg-teal-600 hover:bg-teal-700 text-white font-bold"
              >
                Marcar como lido
              </button>
              <button onClick={() => setShowPreview(false)} className="px-4 py-2 rounded bg-slate-800 text-white font-bold hover:bg-slate-700">Fechar</button>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={!!confirmDeleteId}
        title="Excluir Aviso"
        message="Tem certeza que deseja excluir este aviso? Esta ação não pode ser desfeita."
        onConfirm={handleDelete}
        onCancel={() => setConfirmDeleteId(null)}
        isProcessing={deletingId === confirmDeleteId}
      />
    </div>
  )
}
